import React, { useState, useEffect, useRef } from "react";
import axiosConfig from "../../Service/AxiosConfig";
import DataTable from "react-data-table-component";
import { useDispatch } from "react-redux";
import { updateTempForm } from "../../redux/BookingFormSlice";
import { PopupModal } from "../PopupModal/PopupModal";
import { HiArrowDownTray } from "react-icons/hi2";
import uploadIcon from "../Assets/img-upload.png";
import { AiOutlineFileJpg } from "react-icons/ai";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FaChevronDown } from "react-icons/fa";
import { FaRegFilePdf } from "react-icons/fa6";
import { FaTrashAlt } from "react-icons/fa";
import ShowDeleteModal from "../PopupModal/ShowDeleteModal";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/pdf",
];

const MedicalHistory = ({ form, setForm, isLocked }) => {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState([]);
  const [newDocs, setNewDocs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [editModePatient, setEditModePatient] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [popupType, setPopupType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const fieldsToCheck = ["medical_notes", "documents"];
  const firstFieldRef = useRef(null);
  const hasFocusedOnce = useRef(false);
  const hasAnyData =
    (form?.medical_notes && form.medical_notes.trim() !== "") ||
    documents.length > 0 ||
    newDocs.length > 0;

  useEffect(() => {
    if (!form?.id || isLocked) return;

    if (!isDirty) {
      setEditModePatient(!hasAnyData);
    }
  }, [form?.id, isLocked]);

  useEffect(() => {
    if (!expanded) return;
    const isApiDataEmpty = fieldsToCheck.every(
      (key) => !form?.[key] || String(form[key]).trim() === "",
    );
    if ((isApiDataEmpty || !hasFocusedOnce.current) && firstFieldRef.current) {
      setTimeout(() => {
        firstFieldRef.current.focus();
        hasFocusedOnce.current = true;
      }, 0);
    }
  }, [expanded, form]);

  useEffect(() => {
    if (!form?.documents) return;

    const normalized = form.documents.map((doc) => {
      const d = new Date(doc.cdate);
      return {
        id: doc.id,
        name: doc.document_name,
        preview: doc.document_file,
        cdate:
          d.toLocaleDateString("en-GB") +
          " - " +
          d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        isNew: false,
      };
    });

    setDocuments(normalized);
  }, [form?.documents]);

  useEffect(() => {
    if (expanded && editModePatient) {
      setTimeout(() => {
        firstFieldRef.current?.focus();
      }, 150);
    }
  }, [expanded, editModePatient]);

  useEffect(() => {
    return () => {
      documents.forEach((doc) => {
        if (doc.isNew && doc.preview) {
          URL.revokeObjectURL(doc.preview);
        }
      });
    };
  }, [documents]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!editModePatient) {
      setEditModePatient(true);
    }
    setIsDirty(true);
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    dispatch(
      updateTempForm({
        booking_id: form.booking_id,
        data: { [name]: value },
      }),
    );
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png"].includes(ext))
      return <AiOutlineFileJpg size={18} />;
    if (ext === "pdf") return <FaRegFilePdf size={18} />;
    return "📁";
  };
  const handleLocalDelete = (id) => {
    const file = documents.find((doc) => doc.id === id);

    if (file?.isNew && file.preview) {
      URL.revokeObjectURL(file.preview);
    }

    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    setNewDocs((prev) => prev.filter((doc) => doc.id !== id));
  };
  const handleUploadSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = [];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setPopupType("error");
        setError("Only JPG, PNG or PDF files are allowed");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setPopupType("error");
        setError("File size should be under 5MB");
        return;
      }

      const now = new Date();
      validFiles.push({
        id: Date.now() + Math.random(),
        name: file.name,
        file,
        preview: URL.createObjectURL(file),
        cdate:
          now.toLocaleDateString("en-GB") +
          " - " +
          now.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        isNew: true,
      });
    }

    setDocuments((prev) => [...prev, ...validFiles]);
    setNewDocs((prev) => [...prev, ...validFiles]);
    setIsDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = new FormData();
      payload.append("medical_notes", form.medical_notes || "");

      newDocs.forEach((doc, i) => {
        payload.append(`documents[${i}].file`, doc.file);
        payload.append(`documents[${i}].type`, doc.file.name);
      });

      await axiosConfig.patch(`/accounts/patients/${form?.id}/`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const refreshed = await axiosConfig.get(
        `/accounts/patients/${form?.id}/`,
      );

      setForm((p) => ({
        ...p,
        documents: refreshed?.data?.documents || [],
      }));

      setNewDocs([]);
      setEditModePatient(false);
      setIsDirty(false);
      setExpanded(false);
      setPopupType("success");
      setMessage("Medical History Saved Successfully");
    } catch (err) {
      console.error("Save failed", err);
      setPopupType("error");
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };
  const confirmDelete = async () => {
    try {
      await axiosConfig.delete(`/accounts/patient-documents/${deleteId}/`);

      setDocuments((prev) => prev.filter((d) => d.id !== deleteId));
      setNewDocs((prev) => prev.filter((d) => d.id !== deleteId));

      setPopupType("success");
      setMessage("Document deleted successfully");
    } catch {
      setPopupType("error");
      setError("Delete failed");
    } finally {
      setShowDeletePopup(false);
    }
  };

  const columns = [
    {
      name: "S.No",
      selector: (_, index) => String(index + 1).padStart(2, "0"),
      width: "70px",
    },
    {
      name: "File Name",
      cell: (row) => (
        <div style={{ display: "flex", gap: 8 }}>
          {getFileIcon(row.name)}
          {row.name}
        </div>
      ),
    },
    {
      name: "Date & Time",
      selector: (row) => row.cdate,
      hide: "sm",
    },
    {
      name: "Actions",
      maxWidth: "120px",
      cell: (row) => (
        <div style={{ display: "flex", gap: 12 }}>
          <button
            aria-label="Delete document"
            disabled={!editModePatient || isLocked}
            onClick={() => {
              if (!editModePatient) return;

              if (row.isNew) {
                handleLocalDelete(row.id);
              } else {
                setDeleteId(row?.id);
                setShowDeletePopup(true);
              }
            }}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <FaTrashAlt
              size={14}
              color={editModePatient && !isLocked ? "red" : "#ccc"}
            />
          </button>

          {!row.isNew && (
            <a href={row.preview} download>
              <HiArrowDownTray size={18} />
            </a>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["patient"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("patient"));
        }}
      >
        <AccordionItem uuid="patient">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Medical History</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    marginLeft: "5px",
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel className="bd-panel">
            <div
              className={`bd-section-grid bd-grid-3 service-cards ${
                isLocked ? "locked-section" : ""
              }`}
            >
              <div className="form-group full-width">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Medical Condition / Complaints
                </label>
                <textarea
                  name="medical_notes"
                  rows={3}
                  ref={firstFieldRef}
                  value={form?.medical_notes || ""}
                  onChange={handleChange}
                  disabled={!editModePatient || isLocked}
                  className={`form-control ${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="form-group full-width">
                <div
                  className="document-upload-box"
                  onClick={() => {
                    if (!editModePatient || isLocked) return;
                    fileInputRef.current?.click();
                  }}
                  style={{
                    cursor:
                      editModePatient && !isLocked ? "pointer" : "not-allowed",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleUploadSelect}
                    disabled={!editModePatient || isLocked}
                  />

                  <img
                    src={uploadIcon}
                    alt="upload"
                    className="document-upload-icon"
                  />
                  <small>Click here to upload or Drag your reports here</small>
                  <p>Upload Your Health Reports</p>
                </div>

                <DataTable
                  columns={columns}
                  data={documents}
                  dense
                  highlightOnHover
                  progressPending={saving}
                  noDataComponent="No documents uploaded"
                />
              </div>

              <div className="form-group full-width">
                <div className="bd-actions">
                  {!isLocked && !editModePatient ? (
                    <button
                      className="bd-edit-btn"
                      onClick={() => {
                        setEditModePatient(true);
                        setIsDirty(true);
                        setExpanded(true);
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    !isLocked &&
                    hasAnyData &&
                    editModePatient && (
                      <button
                        className="bd-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        Save
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>

      {showDeletePopup && (
        <ShowDeleteModal
          open={showDeletePopup}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeletePopup(false)}
        />
      )}
      {popupType && (
        <PopupModal
          setpopupType={setPopupType}
          popupType={popupType}
          error={error}
          Message={message}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default MedicalHistory;
